import {
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { RemoveScroll } from "react-remove-scroll";
import { Button } from "./Button";

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

// NOTE: `prefersReducedMotion` はモジュールロード時に 1 度だけ評価されるため、
// ユーザーが実行中に OS の reduced-motion 設定を変更しても追従しない。
// 現在の利用実態（SPA で設定変更頻度は極低い）では許容しているが、
// WCAG 2.3.3「Animation from Interactions」をより厳密に満たしたい場合は
// `useSyncExternalStore` ベースのカスタムフックで購読する方が堅い。
// 将来対応は別 issue で追跡する想定（reviewer Suggestion 1）。
const prefersReducedMotion =
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;
// CSS（index.css）の modal-exit / backdrop-exit の duration と同期が必要
const EXIT_ANIMATION_MS = prefersReducedMotion ? 0 : 150;

// ModalProps: `title` または `ariaLabel` のいずれかを必須化する
// discriminated union。`role="dialog"` は WAI-ARIA 1.1 で accessible name が
// 必須（WCAG 2.1 SC 4.1.2 AA 相当）のため、型レベルで抜け漏れを防ぐ。
// - `title` 指定時は `aria-labelledby` で可視タイトルを参照
// - `ariaLabel` 指定時は `aria-label` を直接付与（見出しを表示しないパターン用）
//
// 既存呼び出しはすべて `title` を渡しているため破壊変更なし（audit-a11y Minor Min-1）。
type BaseModalProps = {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg";
};

type ModalProps = BaseModalProps &
  (
    | { title: string; ariaLabel?: never }
    | { title?: undefined; ariaLabel: string }
  );

const sizeStyles = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
};

export function Modal({
  isOpen,
  onClose,
  title,
  ariaLabel,
  children,
  footer,
  size = "md",
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const [isRendered, setIsRendered] = useState(false);
  const closingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isClosing = !isOpen && isRendered;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }

      if (event.key !== "Tab" || !modalRef.current) {
        return;
      }

      const focusableElements =
        modalRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusableElements.length === 0) {
        return;
      }

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    },
    [onClose]
  );

  // isOpen→isRendered の遅延同期（退場アニメーション用）
  useEffect(() => {
    if (isOpen) {
      if (closingTimerRef.current) {
        clearTimeout(closingTimerRef.current);
        closingTimerRef.current = null;
      }
      setIsRendered(true);
    } else if (isRendered) {
      closingTimerRef.current = setTimeout(() => {
        setIsRendered(false);
        closingTimerRef.current = null;
      }, EXIT_ANIMATION_MS);
    }
    return () => {
      if (closingTimerRef.current) {
        clearTimeout(closingTimerRef.current);
        closingTimerRef.current = null;
      }
    };
  }, [isOpen, isRendered]);

  // effect A: focus 管理（body scroll lock は RemoveScroll に委譲したため削除）
  // deps から handleKeyDown を外すことで、親が inline onClose を渡しても
  // focus が揺らがない（Modal.tsx 計画書 v2 の責務分離）。
  //
  // rAF race condition 対策: effect 発火後 rAF callback 実行前に cleanup が
  // 走ると、previousFocus を戻した後で rAF が firstFocusable.focus() を呼び
  // フォーカスを奪う (WCAG 2.1 SC 2.4.3)。cancelAnimationFrame + cancelled
  // フラグで必ず cleanup 時に rAF をキャンセルする。
  //
  // Strict Mode での二重実行: 1 回目の cleanup で previousFocusRef.current を
  // null にしているため、2 回目の cleanup は no-op となり idempotent。
  useEffect(() => {
    if (!(isOpen && isRendered)) {
      return;
    }

    previousFocusRef.current = document.activeElement as HTMLElement | null;
    let cancelled = false;
    const rafId = requestAnimationFrame(() => {
      if (cancelled) {
        return;
      }
      const firstFocusable =
        modalRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      firstFocusable?.focus();
    });

    return () => {
      // rAF を確実にキャンセルしてから previousFocus を戻す。
      // これにより cleanup 後に rAF が実行されてフォーカスが奪われる
      // race condition を防ぐ。
      cancelled = true;
      cancelAnimationFrame(rafId);

      // モーダルが閉じる/アンマウントされる時、無条件に previousFocus を戻す。
      // closure で isOpen をキャプチャしないため #107 の root cause が再発しない。
      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
        previousFocusRef.current = null;
      }
    };
  }, [isOpen, isRendered]);

  // effect B: keydown handler（handleKeyDown を deps に含む独立 effect）
  useEffect(() => {
    if (!(isOpen && isRendered)) {
      return;
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, isRendered, handleKeyDown]);

  if (!isRendered) {
    return null;
  }

  // 重要: enabled は (isOpen || isRendered) ではなく isRendered のみでよい。
  // isRendered=true の間は退場アニメーション中でも scroll lock を維持する。
  // これにより現行挙動（閉じる瞬間まで lock 維持）との差分が無くなる。
  //
  // noIsolation: RemoveScroll のデフォルト (isolation 有効) は兄弟要素に
  // aria-hidden="true" を動的付与するため、ToastContainer (aria-live) や
  // BugReporterBanner がスクリーンリーダーに読み上げられなくなる
  // (WCAG 2.1 SC 4.1.3)。aria-modal="true" で背景読み上げは抑制されるため
  // isolation を無効化して aria-live 領域を保護する。
  return (
    <RemoveScroll enabled={isRendered} noIsolation>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        {/* Backdrop: aria-hidden のためキーボードイベント不要（Escape は handleKeyDown で処理） */}
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: aria-hidden backdrop はキーボード操作対象外 */}
        <div
          className={`fixed inset-0 bg-black/50 ${isClosing ? "animate-backdrop-exit" : "animate-backdrop-enter"}`}
          onClick={onClose}
          aria-hidden="true"
        />

        {/* Modal */}
        <div className="flex min-h-full items-center justify-center p-4">
          <div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? titleId : undefined}
            aria-label={title ? undefined : ariaLabel}
            className={`
              relative w-full ${sizeStyles[size]}
              bg-white rounded-xl shadow-xl
              ${isClosing ? "animate-modal-exit" : "animate-modal-enter"}
            `}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {/* Header */}
            {title && (
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h2
                  id={titleId}
                  className="text-lg font-semibold text-gray-900"
                >
                  {title}
                </h2>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-lg p-1"
                  aria-label="閉じる"
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            )}

            {/* Content */}
            <div className="px-6 py-4">{children}</div>

            {/* Footer */}
            {footer && (
              <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                {footer}
              </div>
            )}
          </div>
        </div>
      </div>
    </RemoveScroll>
  );
}

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "primary" | "danger";
  isLoading?: boolean;
  children?: ReactNode;
}

export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "確認",
  cancelText = "キャンセル",
  variant = "primary",
  isLoading = false,
  children,
}: ConfirmModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            {cancelText}
          </Button>
          <Button
            variant={variant === "danger" ? "danger" : "primary"}
            onClick={onConfirm}
            isLoading={isLoading}
          >
            {confirmText}
          </Button>
        </>
      }
    >
      <p className="text-gray-600">{message}</p>
      {children}
    </Modal>
  );
}
